import { useCallback, useMemo, useReducer } from 'react';

// Steps in the import flow — kept as strings for easy debugging / analytics.
export const STEPS = Object.freeze({
  scope: 'scope',
  preview: 'preview',
  progress: 'progress',
  results: 'results',
});

const ORDER = [STEPS.scope, STEPS.preview, STEPS.progress, STEPS.results];

// Per-record decision shape:
//   { mode: 'create' | 'enrich' | 'overwrite' | 'skip', selected_fields?: string[], overwrite_confirmed?: boolean }
//
// The reducer stores decisions keyed by parser_advert_id so PreviewStep's
// grid can select/deselect thousands of records without re-rendering the
// whole tree.
const initialState = {
  step: STEPS.scope,
  // Scope filters (radius + cities + advanced)
  radiusKm: 25,
  filters: {
    cities: [],
    sources: [],
    price_min: null,
    price_max: null,
    types: [],
    hide_imported: true,
  },
  // Preview decisions keyed by parser_advert_id.
  decisions: {},
  // Batch created after Preview -> Progress transition.
  batchId: null,
  // When true, the modal is hidden but the polling stays alive (see
  // BackgroundJobPill).
  isMinimized: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'SET_RADIUS':
      return { ...state, radiusKm: action.radiusKm };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.filters } };
    case 'SET_DECISION': {
      const { parserAdvertId, decision } = action;
      const next = { ...state.decisions };
      if (decision == null) {
        delete next[parserAdvertId];
      } else {
        next[parserAdvertId] = { ...state.decisions[parserAdvertId], ...decision };
      }
      return { ...state, decisions: next };
    }
    case 'SET_DECISIONS_BULK': {
      const next = { ...state.decisions };
      action.entries.forEach(([id, decision]) => {
        if (decision == null) {
          delete next[id];
        } else {
          next[id] = { ...next[id], ...decision };
        }
      });
      return { ...state, decisions: next };
    }
    case 'CLEAR_DECISIONS':
      return { ...state, decisions: {} };
    case 'SET_BATCH_ID':
      return { ...state, batchId: action.batchId, step: STEPS.progress };
    case 'MINIMIZE':
      return { ...state, isMinimized: true };
    case 'RESTORE':
      return { ...state, isMinimized: false };
    case 'RESET':
      return { ...initialState, radiusKm: state.radiusKm };
    default:
      return state;
  }
};

// useImportFlowState — small state machine for the 4-step flow.
export const useImportFlowState = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const goTo = useCallback((step) => dispatch({ type: 'SET_STEP', step }), []);
  const next = useCallback(() => {
    const idx = ORDER.indexOf(state.step);
    if (idx < ORDER.length - 1) dispatch({ type: 'SET_STEP', step: ORDER[idx + 1] });
  }, [state.step]);
  const prev = useCallback(() => {
    const idx = ORDER.indexOf(state.step);
    if (idx > 0) dispatch({ type: 'SET_STEP', step: ORDER[idx - 1] });
  }, [state.step]);

  const setRadius = useCallback(
    (radiusKm) => dispatch({ type: 'SET_RADIUS', radiusKm }),
    []
  );
  const setFilters = useCallback(
    (filters) => dispatch({ type: 'SET_FILTERS', filters }),
    []
  );

  const setDecision = useCallback(
    (parserAdvertId, decision) =>
      dispatch({ type: 'SET_DECISION', parserAdvertId, decision }),
    []
  );
  const setDecisionsBulk = useCallback(
    (entries) => dispatch({ type: 'SET_DECISIONS_BULK', entries }),
    []
  );
  const clearDecisions = useCallback(
    () => dispatch({ type: 'CLEAR_DECISIONS' }),
    []
  );

  const setBatchId = useCallback(
    (batchId) => dispatch({ type: 'SET_BATCH_ID', batchId }),
    []
  );
  const minimize = useCallback(() => dispatch({ type: 'MINIMIZE' }), []);
  const restore = useCallback(() => dispatch({ type: 'RESTORE' }), []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  // Convenience selectors.
  const selectedRecords = useMemo(() => {
    return Object.entries(state.decisions)
      .filter(([, d]) => d?.mode && d.mode !== 'skip')
      .map(([id, d]) => ({ parser_advert_id: Number(id), ...d }));
  }, [state.decisions]);

  const stepIndex = ORDER.indexOf(state.step);

  return {
    ...state,
    stepIndex,
    steps: ORDER,
    selectedRecords,
    goTo,
    next,
    prev,
    setRadius,
    setFilters,
    setDecision,
    setDecisionsBulk,
    clearDecisions,
    setBatchId,
    minimize,
    restore,
    reset,
  };
};
